import axios from 'axios';

// Create axios instance with base configuration
// Use Cloudflare Worker only - no Railway fallback
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://ugc-validation-api.mariusz-labudda.workers.dev',
  timeout: 120000, // 120 seconds timeout for image processing (increased for large files)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);
    
    // If data is FormData, remove Content-Type header to let axios set it automatically
    if (config.data instanceof FormData) {
      if (config.headers) {
        delete config.headers['Content-Type'];
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Tłumaczenia komunikatów błędów API (np. z backendu) na polski
const ERROR_TRANSLATIONS = {
  'file too large': 'Plik jest zbyt duży. Maksymalny rozmiar to 5 MB.',
  'files are too large': 'Pliki są zbyt duże. Maksymalny rozmiar: 5 MB na plik.',
  'too large': 'Plik jest zbyt duży. Maks. 5 MB.',
  'invalid file': 'Nieprawidłowy plik.',
  'invalid file type': 'Nieprawidłowy format. Dozwolone: JPG, PNG, WebP.',
  'invalid files': 'Nieprawidłowe pliki. Sprawdź format i rozmiar (max 5 MB).',
  'minimum 1 image': 'Dodaj minimum 3 zdjęcia.',
  'at least 3': 'Dodaj minimum 3 zdjęcia.',
  'required': 'Wypełnij wszystkie wymagane pola.',
  'request entity too large': 'Pliki są zbyt duże. Maks. 5 MB na plik.',
  'payload too large': 'Pliki są zbyt duże. Maks. 5 MB na plik.',
};
function translateApiError(msg) {
  if (!msg || typeof msg !== 'string') return msg;
  const largerThanMatch = msg.match(/^(.*?):\s*File is larger than \d+ bytes\.?$/i);
  if (largerThanMatch) {
    const filename = largerThanMatch[1]?.trim();
    return `${filename}: Plik jest zbyt duży. Maksymalny rozmiar to 5 MB.`;
  }

  if (/^File is larger than \d+ bytes\.?$/i.test(msg)) {
    return 'Plik jest zbyt duży. Maksymalny rozmiar to 5 MB.';
  }

  const lower = msg.toLowerCase();
  for (const [en, pl] of Object.entries(ERROR_TRANSLATIONS)) {
    if (lower.includes(en)) return pl;
  }
  return msg;
}

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log(`Response from ${response.config.url}:`, response.status);
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    
    if (error.response?.status === 429) {
      throw new Error('Zbyt wiele prób. Spróbuj ponownie za godzinę.');
    }
    
    if (error.response?.status === 413) {
      throw new Error('Pliki są zbyt duże. Maksymalny rozmiar: 5 MB na plik.');
    }
    
    if (error.response?.status === 400) {
      const data = error.response.data;
      if (data?.rejectedImages && data.rejectedImages.length > 0) {
        const msg = data.error || 'Zdjęcia zostały odrzucone';
        const errorWithDetails = new Error(translateApiError(msg) || msg);
        errorWithDetails.rejectedImages = data.rejectedImages;
        errorWithDetails.summary = data.summary;
        throw errorWithDetails;
      }
      const raw = data?.message || data?.error;
      throw new Error(translateApiError(raw) || raw || 'Nieprawidłowe dane wejściowe.');
    }
    
    if (error.response?.status >= 500) {
      const raw = error.response.data?.message || error.response.data?.error;
      throw new Error(translateApiError(raw) || raw || 'Błąd serwera. Spróbuj ponownie za chwilę.');
    }
    
    if (error.code === 'ECONNABORTED') {
      throw new Error('Przekroczono limit czasu. Spróbuj ponownie.');
    }
    
    if (!error.response) {
      throw new Error('Brak połączenia z serwerem. Sprawdź internet.');
    }
    
    const raw = error.response.data?.message || error.response.data?.error || '';
    throw new Error(translateApiError(raw) || raw || 'Wystąpił nieoczekiwany błąd.');
  }
);

/**
 * Validates images using the UGC validation API
 * @param {File[]} files - Array of image files to validate
 * @param {string} textReview - Text review of the product
 * @param {string} orderEmail - Order email address
 * @param {string} orderNumber - Order number
 * @param {Object} fixModeData - Data for fix mode (rejectedImages, acceptedImages)
 * @param {string} customerName - Customer name for the review
 * @param {number} starRating - Star rating from 1 to 5
 * @returns {Promise<Object>} Validation results
 */
export const validateImages = async (files, textReview, orderEmail, orderNumber, fixModeData = null, customerName = '', starRating = 0) => {
  try {
    const formData = new FormData();
    
    // Add files to form data
    files.forEach((file, index) => {
      formData.append('images', file);
    });
    
    // Add text review
    formData.append('textReview', textReview);
    
    // Add order data
    formData.append('orderEmail', orderEmail);
    formData.append('orderNumber', orderNumber);
    
    // Add review data
    formData.append('customerName', customerName);
    formData.append('starRating', starRating.toString());
    
    // Add fix mode data if provided
    if (fixModeData) {
      formData.append('fixMode', 'true');
      formData.append('rejectedImages', JSON.stringify(fixModeData.rejectedImages));
      formData.append('acceptedImages', JSON.stringify(fixModeData.acceptedImages));
    }

    // Don't set Content-Type manually - axios will set it automatically with boundary
    // Remove default Content-Type header for this request to let axios handle FormData
    const response = await api.post('/api/ugc/validate', formData, {
      headers: {
        // Explicitly remove Content-Type to let axios set it automatically with boundary
        'Content-Type': undefined,
      },
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Gets the status of the validation system
 * @returns {Promise<Object>} System status
 */
export const getSystemStatus = async () => {
  try {
    const response = await api.get('/ugc/status');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Health check endpoint
 * @returns {Promise<Object>} Health status
 */
export const healthCheck = async () => {
  try {
    const response = await api.get('/health');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default api;

